import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LogOut, FileText, Activity, Users, Menu, X, UserCog, BarChart3, MapPin, Map, Clock, AlertTriangle, ChevronDown, ChevronRight, FolderKanban, Layers, RefreshCcw, ShieldCheck, UserCheck, ScrollText, FolderOpen, HardDrive } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { OfflineReadyBadge, OfflineBanner, OfflineStatusPanel } from '../components/OfflineComponents';
import { useOffline } from '../hooks/useOffline';
import NotificacionesDropdown from '../components/NotificacionesDropdown';
import EmpresaTour, { EmpresaTourBanner } from '../components/EmpresaTour';
import ServerMonitor from '../components/admin/ServerMonitor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente para sección colapsable del menú
function MenuSection({ title, icon: Icon, children, isOpen, onToggle, accentColor = 'emerald', testId }) {
  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-800/30',
      text: 'text-emerald-200',
      iconBg: 'bg-emerald-700/50'
    },
    amber: {
      bg: 'bg-amber-900/20',
      text: 'text-amber-200',
      iconBg: 'bg-amber-700/50'
    }
  };
  
  const colors = colorClasses[accentColor] || colorClasses.emerald;
  
  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger 
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg ${colors.bg} hover:bg-opacity-50 transition-colors mb-1`}
        data-testid={testId}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${colors.iconBg}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className={`font-semibold text-sm ${colors.text}`}>{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-white/60" />
        ) : (
          <ChevronRight className="w-4 h-4 text-white/60" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-1 ml-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

// Componente MenuItem
// Mapeo path → data-tour slug, usado para anclar pasos del tour del rol empresa
// a los items del menú lateral. Se aplica como atributo data-tour en el <Link>
// para tener selectores estables (no dependen de clases ni texto que pueda cambiar).
const TOUR_SLUG_BY_PATH = {
  '/dashboard': 'menu-inicio',
  '/dashboard/peticiones': 'menu-mis-peticiones',
  '/dashboard/predios': 'menu-consulta-predios',
  '/dashboard/visor-predios': 'menu-visor-predios',
};

function MenuItem({ item, isActive, onNavigate }) {
  const Icon = item.icon;
  const showBadge = item.badge && item.badge > 0;
  const tourSlug = TOUR_SLUG_BY_PATH[item.path];

  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      className={`flex items-center justify-between px-3 py-2 rounded-md transition-colors text-sm ${
        isActive
          ? 'bg-emerald-800 text-white'
          : 'text-emerald-100 hover:bg-emerald-800/50 hover:text-white'
      }`}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      data-tour={tourSlug}
    >
      <div className="flex items-center">
        <Icon className="w-4 h-4 mr-2.5" />
        {item.label}
      </div>
      {showBadge && (
        <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
          {item.badge}
        </span>
      )}
    </Link>
  );
}

export default function DashboardLayout() {
  const { user, logout, loading, showTimeoutWarning, extendSession } = useAuth();
  const { isOnline, offlineData } = useOffline();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cambiosPendientesCount, setCambiosPendientesCount] = useState(0);
  const [showOfflinePanel, setShowOfflinePanel] = useState(false);
  // Tour onboarding del rol empresa.
  const [tourOpen, setTourOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  
  // Estado de secciones colapsables
  const [conservacionOpen, setConservacionOpen] = useState(false);
  const [actualizacionOpen, setActualizacionOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  

  const fetchCambiosPendientes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      // Obtener todos los tipos de pendientes
      const [cambiosRes, prediosNuevosRes, reaparicionesRes, mutacionesRes] = await Promise.all([
        axios.get(`${API}/predios/cambios/stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { total_pendientes: 0 } })),
        axios.get(`${API}/predios-nuevos`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { predios: [] } })),
        axios.get(`${API}/predios/reapariciones/solicitudes-pendientes`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { solicitudes: [] } })),
        axios.get(`${API}/solicitudes-mutacion/pendientes-aprobacion`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { solicitudes: [] } }))
      ]);

      const cambios = cambiosRes.data.total_pendientes || 0;
      const prediosData = prediosNuevosRes.data.predios || [];

      // Para coordinadores: contar predios en revisión
      // Para gestores: contar predios asignados a ellos en estado creado/digitalizacion/devuelto
      const isCoord = user && ['coordinador', 'administrador'].includes(user.role);
      let prediosNuevos = 0;
      if (isCoord) {
        prediosNuevos = prediosData.filter(p => (p.estado_flujo || p.estado) === 'revision').length;
      } else {
        prediosNuevos = prediosData.filter(p =>
          p.gestor_apoyo_id === user?.id &&
          ['creado', 'digitalizacion', 'devuelto'].includes(p.estado_flujo || p.estado)
        ).length;
      }

      const reapariciones = (reaparicionesRes.data.solicitudes || []).length;
      const mutaciones = (mutacionesRes.data.solicitudes || []).length;
      const total = cambios + prediosNuevos + reapariciones + mutaciones;

      setCambiosPendientesCount(total);
    } catch (error) {
      console.error('Error fetching pending changes:', error);
    }
  }, []);

  const checkGdbAlert = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gdb/verificar-alerta-mensual`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.mostrar_alerta) {
        toast.warning('Recordatorio: Es momento de cargar la base gráfica mensual', {
          duration: 10000,
          action: {
            label: 'Ir a Visor',
            onClick: () => window.location.href = '/dashboard/visor-predios'
          }
        });
      }
    } catch (error) {
      console.error('Error checking GDB alert:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      checkGdbAlert();
      if (['administrador', 'coordinador'].includes(user.role) || user.permissions?.includes('approve_changes')) {
        fetchCambiosPendientes();
      }
    }
    
    // Listener para actualizar el badge cuando se aprueba/rechaza un pendiente
    const handlePendientesUpdated = () => {
      if (user && (['administrador', 'coordinador'].includes(user.role) || user.permissions?.includes('approve_changes'))) {
        fetchCambiosPendientes();
      }
    };
    
    window.addEventListener('pendientesUpdated', handlePendientesUpdated);
    return () => window.removeEventListener('pendientesUpdated', handlePendientesUpdated);
  }, [user, checkGdbAlert, fetchCambiosPendientes]);

  // Abre el Portal de Archivos (FileBrowser) vía SSO. Solo coordinador (backend gatea con 403).
  // Abrimos la pestaña SINCRÓNICAMENTE dentro del gesto del click (about:blank) y recién
  // le fijamos la URL tras el await; si se abriera después del await, el bloqueador de
  // popups la cancela y el usuario cae en el portal como "publico".
  const abrirPortalArchivos = useCallback(async () => {
    const win = window.open('about:blank', '_blank');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/filebrowser/launch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data?.url) {
        if (win) win.location.href = data.url;
        else window.location.href = data.url; // fallback si el popup fue bloqueado
      } else {
        if (win) win.close();
        toast.error('No se pudo abrir el Portal de Archivos');
      }
    } catch (error) {
      if (win) win.close();
      console.error('Error abriendo Portal de Archivos:', error);
      toast.error('No se pudo abrir el Portal de Archivos');
    }
  }, []);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Agregar/quitar atributo al body cuando el sidebar está abierto (para CSS)
  useEffect(() => {
    if (sidebarOpen) {
      document.body.setAttribute('data-sidebar-open', 'true');
    } else {
      document.body.removeAttribute('data-sidebar-open');
    }
    return () => document.body.removeAttribute('data-sidebar-open');
  }, [sidebarOpen]);

  // Memoized role names
  const getRoleName = useCallback((role) => {
    const roles = {
      usuario: 'Usuario',
      atencion_usuario: 'Atención al Usuario',
      gestor: 'Gestor',
      gestor_auxiliar: 'Gestor Auxiliar',
      coordinador: 'Coordinador',
      administrador: 'Administrador',
      comunicaciones: 'Comunicaciones',
      profesional_juridica: 'Profesional Jurídica',
      subdireccion: 'Subdirectora'
    };
    return roles[role] || role;
  }, []);

  // Memoized menu items
  const menuData = useMemo(() => {
    if (!user) return { baseMenuItems: [], conservacionItems: [], actualizacionItems: [], adminItems: [] };

    // Subdirección: solo firma la M4 que le asignan. Su Inicio es esa tarjeta y
    // nada más (regla del 2026-09-13), así que el menú no ofrece otra puerta.
    if (user.role === 'subdireccion') {
      return {
        baseMenuItems: [{ path: '/dashboard', label: 'Inicio', icon: Activity }],
        conservacionItems: [], actualizacionItems: [], adminItems: [],
      };
    }

    const isStaff = !['usuario', 'empresa'].includes(user.role);
    const canSeeAllPetitions = isStaff;
    const isCoordAdmin = ['administrador', 'coordinador'].includes(user.role);
    const canManageUsers = ['administrador', 'coordinador', 'atencion_usuario'].includes(user.role);
    // Entra a Actualización quien tiene papel: por rol o porque la mesa le
    // repartió manzanas/predios (lo calcula /auth/me). Sin el dato, el rol.
    const canAccessActualizacion = typeof user.acceso_actualizacion === 'boolean'
      ? user.acceso_actualizacion
      : ['administrador', 'coordinador', 'gestor', 'gestor_auxiliar', 'profesional_juridica'].includes(user.role);
    
    // Verificar si el usuario tiene permiso de aprobar cambios (gestores con este permiso pueden ver Pendientes)
    const userPermissions = user.permissions || [];
    const hasApprovePermission = userPermissions.includes('approve_changes');
    const canSeePendientes = isCoordAdmin || hasApprovePermission;
    
    // Todos los gestores pueden ver "Mis Asignaciones" (predios nuevos o modificaciones asignados a ellos)
    const canSeeMisAsignaciones = ['administrador', 'coordinador', 'gestor', 'atencion_usuario'].includes(user.role);

    const baseMenuItems = [
      { path: '/dashboard', label: 'Inicio', icon: Activity },
      { path: '/dashboard/peticiones', label: 'Mis Peticiones', icon: FileText },
    ];

    const conservacionItems = [];
    if (canSeeAllPetitions) {
      conservacionItems.push({ path: '/dashboard/todas-peticiones', label: 'Todas las Peticiones', icon: Users });
    }
    if (isStaff) {
      conservacionItems.push({ path: '/dashboard/predios', label: 'Gestión de Predios', icon: MapPin });
      conservacionItems.push({ path: '/dashboard/visor-predios', label: 'Visor de Predios', icon: Map });
      conservacionItems.push({ path: '/dashboard/mutaciones-resoluciones', label: 'Mutaciones y Resoluciones', icon: FileText });
      // Certificados solo para administrador, coordinador y atención al usuario
      const canSeeCertificados = ['administrador', 'coordinador', 'atencion_usuario'].includes(user.role);
      if (canSeeCertificados) {
        conservacionItems.push({ path: '/dashboard/certificados', label: 'Certificados', icon: ShieldCheck });
      }
    }
    // Empresa: acceso a consulta de predios (solo lectura de sus municipios asignados)
    if (user.role === 'empresa') {
      conservacionItems.push({ path: '/dashboard/predios', label: 'Consulta de Predios', icon: MapPin });
      conservacionItems.push({ path: '/dashboard/visor-predios', label: 'Visor de Predios', icon: Map });
    }
    // "Mis Asignaciones" se centralizó en Inicio (cards de buckets + RadicadosPanel
    // embebido) para gestor / gestor_auxiliar / profesional_juridica / atencion_usuario.
    // Se removió del menú para forzar el flujo único desde Inicio. La ruta
    // /dashboard/pendientes sigue accesible por URL directa (mismo panel se renderiza).
    // Pendientes (vista del coordinador) tampoco se muestra en el menú lateral —
    // accede vía Inicio igual que los demás roles.

    const actualizacionItems = [];
    if (canAccessActualizacion) {
      actualizacionItems.push({ path: '/dashboard/proyectos-actualizacion', label: 'Proyectos', icon: FolderKanban });
    }

    const adminItems = [];
    if (canManageUsers) {
      adminItems.push({ path: '/dashboard/usuarios', label: 'Gestión de Usuarios', icon: UserCog });
      adminItems.push({ path: '/dashboard/estadisticas', label: 'Estadísticas y Reportes', icon: BarChart3 });
      adminItems.push({ path: '/dashboard/log-actividades', label: 'Log de Actividades', icon: ScrollText });
    }
    if (isCoordAdmin) {
      adminItems.push({ path: '/dashboard/gestor-recursos', label: 'Gestor de Recursos', icon: FolderOpen });
    }

    return { baseMenuItems, conservacionItems, actualizacionItems, adminItems };
  }, [user, cambiosPendientesCount]);

  const { baseMenuItems, conservacionItems, actualizacionItems, adminItems } = menuData;

  // Get current page title
  const currentPageTitle = useMemo(() => {
    const allItems = [...baseMenuItems, ...conservacionItems, ...actualizacionItems, ...adminItems];
    const currentItem = allItems.find(item => item.path === location.pathname);
    return currentItem?.label || 'Dashboard';
  }, [baseMenuItems, conservacionItems, actualizacionItems, adminItems, location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-slate-50">
      {/* Banner de modo offline */}
      {!isOnline && (
        <OfflineBanner onViewData={() => setShowOfflinePanel(true)} />
      )}
      
      <div className="flex flex-1 overflow-hidden">
      {/* Sidebar - Desktop */}
      <div className="hidden md:flex w-64 flex-col bg-emerald-900 text-white border-r border-emerald-800 overflow-y-auto">
        {/* Header con logo */}
        <div className="p-4 border-b border-emerald-800 flex-shrink-0">
          <img 
            src="/logo-asomunicipios.png" 
            alt="Asomunicipios Logo" 
            className="w-24 mx-auto mb-2 rounded"
            data-testid="sidebar-logo"
          />
          <h2 className="text-xs font-bold font-outfit leading-tight text-center" data-testid="sidebar-title">
            Asociación de Municipios del Catatumbo, Provincia de Ocaña y Sur del Cesar
          </h2>
          <p className="text-emerald-200 text-xs mt-1 text-center font-semibold">– Asomunicipios –</p>
          <p className="text-emerald-100 text-xs mt-2 text-center">{getRoleName(user.role)}</p>
        </div>

        {/* Navegación */}
        <nav className="flex-1 p-3 space-y-4 overflow-y-auto" data-testid="sidebar-nav">
          {/* Items base */}
          <div className="space-y-1">
            {baseMenuItems.map(item => (
              <MenuItem key={item.path} item={item} isActive={location.pathname === item.path} />
            ))}
          </div>

          {/* Sección Conservación */}
          {conservacionItems.length > 0 && (
            <MenuSection
              title="Conservación"
              icon={Layers}
              isOpen={conservacionOpen}
              onToggle={setConservacionOpen}
              accentColor="emerald"
              testId="section-conservacion"
            >
              {conservacionItems.map(item => (
                <MenuItem key={item.path} item={item} isActive={location.pathname === item.path} />
              ))}
            </MenuSection>
          )}

          {/* Sección Actualización */}
          {actualizacionItems.length > 0 && (
            <MenuSection
              title="Actualización"
              icon={RefreshCcw}
              isOpen={actualizacionOpen}
              onToggle={setActualizacionOpen}
              accentColor="amber"
              testId="section-actualizacion"
            >
              {actualizacionItems.map(item => (
                <MenuItem key={item.path} item={item} isActive={location.pathname === item.path} />
              ))}
              {/* Portal de Archivos (FileBrowser vía SSO) - solo coordinador */}
              {user.role === 'coordinador' && (
                <button
                  type="button"
                  onClick={abrirPortalArchivos}
                  className="w-full flex items-center px-3 py-2 rounded-md transition-colors text-sm text-emerald-100 hover:bg-emerald-800/50 hover:text-white"
                  data-testid="btn-portal-archivos"
                >
                  <HardDrive className="w-4 h-4 mr-2.5" />
                  Portal de Archivos
                </button>
              )}
            </MenuSection>
          )}

          {/* Sección Administración */}
          {adminItems.length > 0 && (
            <MenuSection
              title="Administración"
              icon={UserCog}
              isOpen={adminOpen}
              onToggle={setAdminOpen}
              accentColor="emerald"
              testId="section-administracion"
            >
              {adminItems.map(item => (
                <MenuItem key={item.path} item={item} isActive={location.pathname === item.path} />
              ))}
            </MenuSection>
          )}
        </nav>

        {/* Footer con info de usuario */}
        <div className="p-4 border-t border-emerald-800 flex-shrink-0" data-tour="user-avatar">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium text-white truncate" data-testid="user-name">{user.full_name}</p>
            <p className="text-xs text-emerald-200 truncate" data-testid="user-email">{user.email}</p>
          </div>
          {/* Opción para repetir el recorrido (solo rol empresa) */}
          {user.role === 'empresa' && (
            <Button
              onClick={() => setTourOpen(true)}
              variant="ghost"
              className="w-full justify-start text-emerald-100 hover:bg-emerald-800 hover:text-white mb-1"
              data-testid="repetir-tour-button"
            >
              <span className="mr-2">🎯</span>
              Ver recorrido
            </Button>
          )}
          <Button
            onClick={logout}
            variant="ghost"
            className="w-full justify-start text-emerald-100 hover:bg-emerald-800 hover:text-white"
            data-testid="logout-button"
          >
            <LogOut aria-hidden="true" className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-[9999] md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={closeSidebar}></div>
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-emerald-900 text-white flex flex-col">
            <div className="absolute right-2 top-2">
              <button aria-label="Cerrar" onClick={closeSidebar} className="text-white p-1 hover:bg-emerald-800 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Header con logo - Mobile */}
            <div className="p-4 border-b border-emerald-800 flex-shrink-0">
              <img 
                src="/logo-asomunicipios.png" 
                alt="Asomunicipios Logo" 
                className="w-24 mx-auto mb-2 rounded"
              />
              <h2 className="text-xs font-bold font-outfit leading-tight text-center">
                Asociación de Municipios del Catatumbo, Provincia de Ocaña y Sur del Cesar
              </h2>
              <p className="text-emerald-200 text-xs mt-1 text-center font-semibold">– Asomunicipios –</p>
              <p className="text-emerald-100 text-xs mt-2 text-center">{getRoleName(user.role)}</p>
            </div>

            {/* Navegación - Mobile */}
            <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
              <div className="space-y-1">
                {baseMenuItems.map(item => (
                  <MenuItem key={item.path} item={item} isActive={location.pathname === item.path} onNavigate={closeSidebar} />
                ))}
              </div>

              {conservacionItems.length > 0 && (
                <MenuSection
                  title="Conservación"
                  icon={Layers}
                  isOpen={conservacionOpen}
                  onToggle={setConservacionOpen}
                  accentColor="emerald"
                  testId="section-conservacion-mobile"
                >
                  {conservacionItems.map(item => (
                    <MenuItem key={item.path} item={item} isActive={location.pathname === item.path} onNavigate={closeSidebar} />
                  ))}
                </MenuSection>
              )}

              {actualizacionItems.length > 0 && (
                <MenuSection
                  title="Actualización"
                  icon={RefreshCcw}
                  isOpen={actualizacionOpen}
                  onToggle={setActualizacionOpen}
                  accentColor="amber"
                  testId="section-actualizacion-mobile"
                >
                  {actualizacionItems.map(item => (
                    <MenuItem key={item.path} item={item} isActive={location.pathname === item.path} onNavigate={closeSidebar} />
                  ))}
                  {/* Portal de Archivos (FileBrowser vía SSO) - solo coordinador */}
                  {user.role === 'coordinador' && (
                    <button
                      type="button"
                      onClick={() => { closeSidebar(); abrirPortalArchivos(); }}
                      className="w-full flex items-center px-3 py-2 rounded-md transition-colors text-sm text-emerald-100 hover:bg-emerald-800/50 hover:text-white"
                      data-testid="btn-portal-archivos-mobile"
                    >
                      <HardDrive className="w-4 h-4 mr-2.5" />
                      Portal de Archivos
                    </button>
                  )}
                </MenuSection>
              )}

              {adminItems.length > 0 && (
                <MenuSection
                  title="Administración"
                  icon={UserCog}
                  isOpen={adminOpen}
                  onToggle={setAdminOpen}
                  accentColor="emerald"
                  testId="section-administracion-mobile"
                >
                  {adminItems.map(item => (
                    <MenuItem key={item.path} item={item} isActive={location.pathname === item.path} onNavigate={closeSidebar} />
                  ))}
                </MenuSection>
              )}
            </nav>

            {/* Footer - Mobile */}
            <div className="p-4 border-t border-emerald-800 flex-shrink-0">
              <div className="px-3 py-2 mb-2">
                <p className="text-sm font-medium text-white truncate">{user.full_name}</p>
                <p className="text-xs text-emerald-200 truncate">{user.email}</p>
              </div>
              <Button
                onClick={logout}
                variant="ghost"
                className="w-full justify-start text-emerald-100 hover:bg-emerald-800 hover:text-white"
              >
                <LogOut aria-hidden="true" className="w-4 h-4 mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        {/* Diálogo de advertencia de timeout */}
        <Dialog open={showTimeoutWarning} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md z-[9999]" onPointerDownOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Sesión por expirar
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Su sesión se cerrará automáticamente en <strong>2 minutos</strong> por inactividad.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 text-center">
              <p className="text-sm text-slate-500">
                ¿Desea continuar trabajando?
              </p>
            </div>
            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button variant="outline" onClick={logout}>
                Cerrar sesión ahora
              </Button>
              <Button 
                onClick={extendSession}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Continuar trabajando
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Header */}
        <header className="h-16 border-b border-slate-200 bg-white flex items-center px-6 justify-between" data-testid="dashboard-header">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden text-slate-700"
              data-testid="mobile-menu-button"
              aria-label="Abrir menú de navegación"
            >
              <Menu className="w-6 h-6" aria-hidden="true" />
            </button>
            <h1 className="text-xl font-semibold text-slate-900 font-outfit" data-testid="page-title">
              {currentPageTitle}
            </h1>
          </div>

          {/* Offline Status y Notificaciones */}
          <div className="flex items-center gap-3">
            {/* Notificaciones */}
            <NotificacionesDropdown />

            {/* Badge de estado offline */}
            {/* El contador de predios offline es del mundo de campo; la Subdirección solo firma M4. */}
            {user?.role !== 'subdireccion' && <OfflineReadyBadge />}
          </div>
        </header>

        {/* Page Content */}
        <main id="main" className="flex-1 p-6 md:p-8">
          {/* Banner de bienvenida + tour para rol empresa (1ª vez) */}
          {!bannerDismissed && (
            <EmpresaTourBanner
              user={user}
              onStart={() => { setBannerDismissed(true); setTourOpen(true); }}
              onDismiss={() => setBannerDismissed(true)}
            />
          )}
          <Outlet />
        </main>
      </div>
      </div>

      {/* Tour interactivo del rol empresa (overlay global) */}
      <EmpresaTour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onFinish={() => { setTourOpen(false); setBannerDismissed(true); }}
      />
      
      {/* Panel de estado offline */}
      <OfflineStatusPanel 
        isOpen={showOfflinePanel} 
        onClose={() => setShowOfflinePanel(false)}
        offlineModules={[
          {
            name: 'Gestión de Predios',
            description: 'Predios descargados para consulta',
            status: offlineData.prediosCount > 0 ? 'ready' : 'none',
            count: offlineData.prediosCount || 0
          },
          {
            name: 'Peticiones',
            description: 'Peticiones de certificados',
            status: offlineData.petitionsCount > 0 ? 'ready' : 'none',
            count: offlineData.petitionsCount || 0
          }
        ]}
      />

      {/* Monitor del servidor en tiempo real (solo admin / coordinador) */}
      <ServerMonitor />
    </div>
  );
}
